import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import RidersManager from './RidersManager';
import './OrderModesForm.css';

const EXTERNAL_PROVIDERS = ['pedidosya', 'rappi', 'ubereats', 'glovo'];

/**
 * Formulario de modos de pedido + configuración de delivery.
 * Contrato alineado con PATCH /restaurants/{id}/settings (NER-1237):
 *   value = {
 *     orderModes: string[]  // subset de ['dine_in','takeaway','delivery']
 *     deliverySettings: { fee, minOrderAmount, maxDistanceKm,
 *                         requireOnlinePayment, allowPayOnDelivery, externalProvider }
 *     restaurantLocation: { lat, lng, address }  // solo lectura: source-of-truth es Edit Restaurant (NER-1262)
 *     ownDelivery: { enabled, riderAssignmentMode }
 *   }
 *
 * La ubicación NO se edita ni se muestra acá (NER-1272): se usa solo para validar que
 * delivery no se active sin ubicación. La única superficie para verla/editarla es
 * Edit Restaurant (tab Location).
 *
 * Props:
 *  - value, onChange(value)
 *  - restaurantId        → para gestión de repartidores + link a Edit Restaurant
 *  - deliveryAvailable   → si el plan incluye delivery (feature gate)
 *  - onDeliveryUpsell()  → callback cuando se intenta activar delivery sin plan
 */
const OrderModesForm = ({
  value,
  onChange,
  restaurantId,
  deliveryAvailable = true,
  onDeliveryUpsell,
}) => {
  const { t } = useTranslation();

  const orderModes = value?.orderModes || [];
  const deliverySettings = value?.deliverySettings || {};
  const location = value?.restaurantLocation || {};
  const ownDelivery = value?.ownDelivery || {};

  const isMode = (mode) => orderModes.includes(mode);
  const deliveryEnabled = isMode('delivery');

  const update = (patch) => {
    onChange({
      orderModes,
      deliverySettings,
      restaurantLocation: location,
      ownDelivery,
      ...patch,
    });
  };

  const toggleMode = (mode) => {
    if (mode === 'delivery' && !isMode('delivery') && !deliveryAvailable) {
      if (onDeliveryUpsell) onDeliveryUpsell();
      return;
    }
    const next = isMode(mode) ? orderModes.filter((m) => m !== mode) : [...orderModes, mode];
    update({ orderModes: next });
  };

  const updateDelivery = (field, val) => {
    update({ deliverySettings: { ...deliverySettings, [field]: val } });
  };

  const updateOwnDelivery = (field, val) => {
    update({ ownDelivery: { ...ownDelivery, [field]: val } });
  };

  const hasLocation =
    location.lat !== undefined &&
    location.lat !== '' &&
    location.lng !== undefined &&
    location.lng !== '';
  const hasProvider = ownDelivery.enabled === true || !!deliverySettings.externalProvider;

  return (
    <div className="order-modes-form">
      {/* Dine In */}
      <div className="order-mode-card">
        <div className="mode-header">
          <div className="mode-title">
            <Icon name="restaurant" />
            <span>{t('orderModes.dineIn.title')}</span>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={isMode('dine_in')}
              aria-label={t('orderModes.dineIn.title')}
              onChange={() => toggleMode('dine_in')}
            />
            <span className="slider round"></span>
          </label>
        </div>
      </div>

      {/* Takeaway */}
      <div className="order-mode-card">
        <div className="mode-header">
          <div className="mode-title">
            <Icon name="shopping_bag" />
            <span>{t('orderModes.takeaway.title')}</span>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={isMode('takeaway')}
              aria-label={t('orderModes.takeaway.title')}
              onChange={() => toggleMode('takeaway')}
            />
            <span className="slider round"></span>
          </label>
        </div>
      </div>

      {/* Delivery */}
      <div className={`order-mode-card ${!deliveryAvailable ? 'mode-locked' : ''}`}>
        <div className="mode-header">
          <div className="mode-title">
            <Icon name="delivery_dining" />
            <span>{t('orderModes.delivery.title')}</span>
            {!deliveryAvailable && (
              <span className="mode-plan-badge">
                <Icon name="lock" />
                {t('orderModes.delivery.planBadge')}
              </span>
            )}
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={deliveryEnabled}
              aria-label={t('orderModes.delivery.title')}
              onChange={() => toggleMode('delivery')}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {deliveryEnabled && (
          <div className="mode-details">
            {!hasLocation && (
              <div className="alert alert-warning mode-alert location-warning">
                <Icon name="warning" />
                <span>{t('orderModes.location.emptyWarning')}</span>
                {restaurantId && (
                  <Link
                    to={`/restaurant/edit/${restaurantId}#location`}
                    className="btn btn-secondary btn-sm location-edit-link"
                  >
                    <Icon name="location_on" />
                    {t('orderModes.location.configureInRestaurant')}
                  </Link>
                )}
              </div>
            )}
            {!hasProvider && (
              <div className="alert alert-warning mode-alert">
                <Icon name="warning" />
                <span>{t('orderModes.delivery.providerRequired')}</span>
              </div>
            )}

            <div className="delivery-settings-grid">
              <div className="form-group">
                <label>{t('orderModes.delivery.fee')}</label>
                <div className="input-with-suffix">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    step="10"
                    value={deliverySettings.fee ?? ''}
                    onChange={(e) =>
                      updateDelivery('fee', e.target.value === '' ? '' : parseFloat(e.target.value))
                    }
                    className="percentage-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('orderModes.delivery.minOrderAmount')}</label>
                <div className="input-with-suffix">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={deliverySettings.minOrderAmount ?? ''}
                    onChange={(e) =>
                      updateDelivery(
                        'minOrderAmount',
                        e.target.value === '' ? '' : parseFloat(e.target.value)
                      )
                    }
                    className="percentage-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('orderModes.delivery.maxDistanceKm')}</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    step="0.5"
                    value={deliverySettings.maxDistanceKm ?? ''}
                    onChange={(e) =>
                      updateDelivery(
                        'maxDistanceKm',
                        e.target.value === '' ? '' : parseFloat(e.target.value)
                      )
                    }
                    className="percentage-input"
                  />
                  <span className="input-suffix">km</span>
                </div>
              </div>
            </div>

            <div className="mode-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={deliverySettings.requireOnlinePayment === true}
                  onChange={(e) => updateDelivery('requireOnlinePayment', e.target.checked)}
                />
                <span>{t('orderModes.delivery.requireOnlinePayment')}</span>
              </label>
            </div>
            <div className="mode-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={deliverySettings.allowPayOnDelivery === true}
                  onChange={(e) => updateDelivery('allowPayOnDelivery', e.target.checked)}
                />
                <span>{t('orderModes.delivery.allowPayOnDelivery')}</span>
              </label>
            </div>

            <div className="form-group">
              <label>{t('orderModes.delivery.externalProvider')}</label>
              <select
                className="time-select"
                value={deliverySettings.externalProvider || ''}
                onChange={(e) => updateDelivery('externalProvider', e.target.value || undefined)}
              >
                <option value="">{t('orderModes.delivery.providers.none')}</option>
                {EXTERNAL_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {t(`orderModes.delivery.providers.${p}`)}
                  </option>
                ))}
              </select>
              <p className="field-hint">{t('orderModes.delivery.externalProviderHint')}</p>
            </div>

            {/* Delivery propio */}
            <div className="own-delivery-block">
              <div className="own-delivery-header">
                <div className="mode-title">
                  <Icon name="two_wheeler" />
                  <span>{t('orderModes.ownDelivery.title')}</span>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={ownDelivery.enabled === true}
                    aria-label={t('orderModes.ownDelivery.title')}
                    onChange={(e) => updateOwnDelivery('enabled', e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>

              {ownDelivery.enabled && (
                <>
                  <div className="form-group">
                    <label>{t('orderModes.ownDelivery.riderAssignmentMode')}</label>
                    <select
                      className="time-select"
                      value={ownDelivery.riderAssignmentMode || 'manual'}
                      onChange={(e) => updateOwnDelivery('riderAssignmentMode', e.target.value)}
                    >
                      <option value="manual">{t('orderModes.ownDelivery.modes.manual')}</option>
                      <option value="auto">{t('orderModes.ownDelivery.modes.auto')}</option>
                    </select>
                  </div>
                  <RidersManager restaurantId={restaurantId} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderModesForm;
